import * as PIXI from 'pixi.js-legacy';
import gsap from '../../gsap';
import { isDefined, valueToHex } from '../utils';

class Line {
  duration;
  delay;
  startPos = { x: 0, y: 0 };
  direction = 1;
  lineData = {
    to: 0,
    reset: true,
    set: values => (this.lineData = { ...this.lineData, ...values }),
  };
  onChange = [];
  isDrawing = false;

  // Constructor
  constructor(
    container,
    positions,
    maxLength,
    instructions,
    thickness,
    alpha = 1,
    color = 0,
    duration = 1000,
    delay = 0,
  ) {
    this.duration = duration;
    this.delay = delay;
    this.thickness = thickness;
    this.maxLength = maxLength;

    this.lineData.set({ alpha, color });

    this.line = new PIXI.Graphics();
    this.line.interactiveChildren = false;

    this.inset = instructions.inset;

    this.resetLine();
    this.setup(positions);
    this.instructions = instructions;

    this.container = container;
    this.container.addChild(this.line);
  }

  makeClickable(callback) {
    this.line.interactive = true;
    this.line.buttonMode = true;
    // TODO we need to calculate & set hit area - see: https://github.com/pixijs/pixijs/issues/3296
    this.line.on('mouseover', () => callback(this));
  }

  setup({ x = 0, y = 0 }) {
    this.line.moveTo(x, y);
    this.startPos = { x, y };
  }

  resetLine(reset = true) {
    if (!isDefined(this.line)) return;
    if (reset) this.line.clear();
    this.setLineStyle();
  }

  setLineStyle() {
    let alignment = 0.5;

    if (isDefined(this.inset)) {
      alignment = this.inset ? 0 : 1;
    }

    this.line.lineStyle({
      width: this.thickness,
      color: valueToHex(this.lineData.color),
      alpha: this.lineData.alpha,
      alignment,
    });
  }

  setOnChange(onChange) {
    this.onChange.push(onChange);
  }

  getPoints() {
    return this.line.currentPath.points;
  }

  toggleDirection() {
    this.direction *= -1;
    const points = this.getPoints();

    this.startPos = {
      x: points[2] || points[0] || 0,
      y: points[3] || points[1] || 0,
    };
  }

  async animateColor(
    color,
    drawFull,
    duration = this.duration,
    delay = this.delay,
  ) {
    await new Promise(resolve => {
      gsap.to(this.lineData, {
        color,
        duration: duration / 1000,
        onUpdate: () => {
          this.onUpdate({
            ...this.lineData,
            to: drawFull ? this.lineData.endDraw.to : undefined,
          });
        },
        onComplete: resolve,
        delay: delay / 1000,
      });
    });
  }

  async animateAlpha(
    alpha,
    drawFull,
    duration = this.duration,
    delay = this.delay,
  ) {
    return new Promise(resolve => {
      gsap.to(this.lineData, {
        alpha,
        ease: 'nikeEase',
        duration: duration / 1000,
        onUpdate: () => {
          this.onUpdate({
            ...this.lineData,
            to: drawFull ? this.lineData.endDraw.to : undefined,
          });
        },
        onComplete: resolve,
        delay: delay / 1000,
      });
    });
  }

  getPosition() {
    return {
      x: this.startPos.x + this.line.x,
      y: this.startPos.y + this.line.y,
      radius: this.lineData.radius
    };
  }

  // Move
  async move(
    { x = this.startPos.x, y = this.startPos.y },
    delay = this.delay,
    duration = this.duration,
  ) {
    return new Promise(resolve => {
      gsap.to(this.line, {
        x: x - this.startPos.x,
        y: y - this.startPos.y,
        ease: 'nikeEase',
        duration: duration / 1000,
        onComplete: resolve,
        onUpdate: () => {
          this.onChange.forEach(onChange => onChange(this.getPosition()));
        },
        delay: delay / 1000,
      });
    });
  }

  // Draw over time
  async draw(to, duration = this.duration, delay = this.delay) {
    return new Promise(resolve => {
      this.lineData.endDraw = to;
      const values = Object.keys(to)
        .map(key => ({ [key]: to[key] }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {});

      if (duration === 0 || duration === 1) {
        if(delay === 0){
          this.onUpdate({ ...this.lineData, ...to });
          return resolve();
        }
        setTimeout(() => {
          // Do not animate if duration is 0
          this.onUpdate({ ...this.lineData, ...to });
          return resolve();
        }, delay);
      } else {
        this.isDrawing = true;
        gsap.to(this.lineData, {
          ...values,
          delay: delay / 1000,
          duration: duration / 1000,
          ease: 'nikeEase',
          onUpdate: this.onUpdate.bind(this),
          onUpdateParams: [this.lineData],
          onComplete: () => {
            this.isDrawing = false;
            resolve();
          },
        });
      }
    });
  }

  async updateStartPosition(
    values,
    duration = this.duration,
    delay = this.delay,
  ) {
    return new Promise(resolve => {
      gsap.to(this.startPos, {
        ...values,
        delay: delay / 1000,
        duration: duration / 1000,
        ease: 'nikeEase',
        onComplete: resolve,
      });
    });
  }

  remove() {
    try{
      this.line.clear()
    }catch(e){
      console.log(e)
    }
    this.container.removeChild(this.line);
  }
}

export default Line;
